import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { GitCompare, RotateCcw } from "lucide-react";
import { TEMPLATES } from "@/constants/testIds";
import { VersionSelect } from "@/components/templates/VersionSelect";
import { MetaChanges } from "@/components/templates/MetaChanges";
import { HtmlDiffView } from "@/components/templates/HtmlDiffView";
import { RenderedCompare } from "@/components/templates/RenderedCompare";

export function VersionCompareDialog({ baseVersion, onOpenChange, versions, previewUserId, onRestore }) {
  const open = baseVersion != null;
  const latest = versions[0]?.version;
  const [leftV, setLeftV] = useState(baseVersion);
  const [rightV, setRightV] = useState(latest);
  const [tab, setTab] = useState("rendered");

  useEffect(() => {
    if (open) { setLeftV(baseVersion); setRightV(latest); }
  }, [open, baseVersion, latest]);

  const left = versions.find((v) => v.version === leftV);
  const right = versions.find((v) => v.version === rightV);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white max-w-6xl w-[96vw] max-h-[92vh] overflow-auto" data-testid={TEMPLATES.compareDialog}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <GitCompare className="w-4 h-4 text-orange-600" strokeWidth={1.75} /> Comparar versiones
          </DialogTitle>
        </DialogHeader>
        {left && right && (
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <VersionSelect label="Versión seleccionada" tone="text-red-700" value={leftV} versions={versions}
                onChange={setLeftV} testId={TEMPLATES.compareLeftSelect} />
              <VersionSelect label="Comparar con" tone="text-emerald-700" value={rightV} versions={versions}
                onChange={setRightV} testId={TEMPLATES.compareRightSelect} />
            </div>
            <MetaChanges left={left} right={right} />
            <Tabs value={tab} onValueChange={setTab}>
              <TabsList className="bg-slate-100">
                <TabsTrigger value="rendered" data-testid={TEMPLATES.compareTab("rendered")}>Vista renderizada</TabsTrigger>
                <TabsTrigger value="code" data-testid={TEMPLATES.compareTab("code")}>Código HTML</TabsTrigger>
              </TabsList>
              <TabsContent value="rendered" className="mt-3">
                <RenderedCompare left={left} right={right} userId={previewUserId} enabled={open && tab === "rendered"} />
              </TabsContent>
              <TabsContent value="code" className="mt-3">
                <HtmlDiffView oldHtml={left.html_content} newHtml={right.html_content} />
              </TabsContent>
            </Tabs>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="border-slate-200">Cerrar</Button>
          <Button onClick={() => onRestore(leftV)} disabled={leftV === latest}
            className="bg-orange-600 hover:bg-orange-700 text-white" data-testid={TEMPLATES.compareRestoreBtn}>
            <RotateCcw className="w-3.5 h-3.5 mr-1.5" strokeWidth={1.75} /> Restaurar v{leftV}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
